import React, { useState, useEffect } from 'react';
import {
    Button,
    Typography,
    Box,
    Paper,
    Divider,
    Chip,
    LinearProgress,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Grid,
    Card,
    CardContent,
    useTheme,
    Fade,
    Slide
} from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SolarPowerIcon from '@mui/icons-material/SolarPower';
import EnergySavingsLeafIcon from '@mui/icons-material/EnergySavingsLeaf';
import ElectricBoltIcon from '@mui/icons-material/ElectricBolt';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import ParkIcon from '@mui/icons-material/Park';
import ScheduleIcon from '@mui/icons-material/Schedule';
import {
    ValorKWService,
    RecursoSolarService,
    EmpresaService
} from '../../api/api';

const Paso2BResultadosParciales = ({ onNext, onBack, data, onWhatsAppClick }) => {
    const [ahorroMensual, setAhorroMensual] = useState(null);
    const [panelesEstimados, setPanelesEstimados] = useState(null);
    const [potenciaInstalada, setPotenciaInstalada] = useState(null);
    const [ahorroCO2, setAhorroCO2] = useState(null);
    const [totalProyecto, setTotalProyecto] = useState(null);
    const [empresaPrestadora, setEmpresaPrestadora] = useState(null);
    const [loadingCalculos, setLoadingCalculos] = useState(true);
    const [errorCalculos, setErrorCalculos] = useState(null);
    const [apiCallsCompleted, setApiCallsCompleted] = useState(0);
    const [showDetails, setShowDetails] = useState(false);
    const theme = useTheme();



    const handleWhatsAppClick = () => {
        // Construir el mensaje con todos los datos
        let message = "¡Hola! Estoy interesado en un proyecto solar. Aquí están mis datos:\n\n";

        // Datos básicos del formulario
        message += `*Datos Personales:*\n`;
        message += `- Nombre: ${data.nombre || 'No especificado'}\n`;
        message += `- Teléfono: ${data.telefono || 'No especificado'}\n`;
        message += `- Correo: ${data.correo || 'No especificado'}\n\n`;

        message += `*Ubicación:*\n`;
        message += `- Departamento: ${data.departamento?.Nombre || 'No especificado'}\n`;
        message += `- Municipio: ${data.municipio?.Nombre || 'No especificado'}\n`;
        message += `- Estrato: ${data.estrato?.Nombre || 'No especificado'}\n\n`;

        message += `*Consumo Eléctrico:*\n`;
        message += `- Valor factura: ${data.valorFactura || 'No especificado'}\n\n`;

        // Resultados calculados
        message += `*Resultados Preliminares:*\n`;
        message += `- Ahorro mensual estimado: ${ahorroMensual ? formatCurrency(ahorroMensual) : 'No calculado'}\n`;
        message += `- Número de paneles estimados: ${panelesEstimados !== null ? panelesEstimados : 'No calculado'}\n`;
        message += `- Potencia instalada: ${potenciaInstalada !== null ? `${formatNumber(potenciaInstalada, 2)} kWp` : 'No calculado'}\n`;
        message += `- Ahorro de CO₂: ${ahorroCO2 !== null ? formatCO2(ahorroCO2) : 'No calculado'}\n`;
        message += `- Árboles salvados: ${ahorroCO2 !== null ? calcularArboles(ahorroCO2) : 'No calculado'}\n`;
        message += `- Recuperación de inversión: ${calcularROI(totalProyecto, ahorroMensual)}\n`;
        message += `- Empresa prestadora: ${empresaPrestadora?.Nombre || 'No identificada'}\n\n`;

        message += `Por favor contácteme para más información. ¡Gracias!`;

        // Codificar el mensaje para URL
        const encodedMessage = encodeURIComponent(message);

        // Crear el enlace de WhatsApp
        const whatsappUrl = `https://wa.me/573154340012?text=${encodedMessage}`;

        // Abrir WhatsApp en una nueva pestaña
        window.open(whatsappUrl, '_blank');

        // También ejecutar el callback si existe
        if (onWhatsAppClick) {
            onWhatsAppClick();
        }
    };



    // Constantes para los cálculos
    const paneles = 550;
    const coeficiente = 1.2;
    const factorCO2 = 0.388;
    const totalApiCalls = 3;

    useEffect(() => {
        const calcularResultados = async () => {
            try {
                setLoadingCalculos(true);
                setErrorCalculos(null);
                setApiCallsCompleted(0);

                // 1. Calcular ahorro mensual
                const valorFacturaNumerico = parseFloat(data.valorFactura?.replace(/[^0-9]/g, '') || '0');
                setAhorroMensual(valorFacturaNumerico * 0.95);

                // 2. Obtener empresa prestadora
                let empresaPrestadoraData = null;
                if (data.departamento?.Id) {
                    const empresasResponse = await EmpresaService.obtenerPorDepartamento(data.departamento.Id);
                    setApiCallsCompleted(prev => prev + 1);

                    if (empresasResponse.data?.length > 0) {
                        empresaPrestadoraData = empresasResponse.data[0];
                        setEmpresaPrestadora(empresaPrestadoraData);
                    } else {
                        throw new Error('No se encontró empresa prestadora para este departamento');
                    }
                }

                // 3. Calcular número de paneles estimados
                if (data.municipio?.Id && empresaPrestadoraData?.Id && data.estrato?.Id) {
                    const [valorKWResponse, recursoSolarResponse] = await Promise.all([
                        ValorKWService.obtenerPorEmpresaEstrato(empresaPrestadoraData.Id, data.estrato.Id),
                        RecursoSolarService.obtenerPorCiudad(data.municipio.Id)
                    ]);

                    setApiCallsCompleted(prev => prev + 2);

                    const valorKW = valorKWResponse.data[0]?.Valor;
                    const recursoSolar = recursoSolarResponse.data[0]?.Valor;

                    if (!valorKW || !recursoSolar) {
                        throw new Error(!valorKW ? 'No se encontró valor KW' : 'No se encontró recurso solar');
                    }

                    const consumoDiario = valorFacturaNumerico / valorKW / 30;
                    const energiaNecesaria = consumoDiario / recursoSolar * 1000;
                    const panelesCalculados = Math.ceil(energiaNecesaria / paneles * coeficiente);

                    const potencia = (panelesCalculados * 500) / 1000;
                    const co2 = (potencia * recursoSolar * 30 * 0.8 * 12) / 1000 / factorCO2;
                    const totalProyectoCalc = panelesCalculados * (data.municipio.Kwp || 5000000);

                    setPanelesEstimados(panelesCalculados);
                    setPotenciaInstalada(potencia);
                    setAhorroCO2(co2);
                    setTotalProyecto(totalProyectoCalc);
                }
            } catch (error) {
                console.error('Error en cálculos:', error);
                setErrorCalculos(error.message || 'Error al realizar los cálculos');
            } finally {
                setLoadingCalculos(false);
            }
        };

        if (data.valorFactura && data.departamento) {
            calcularResultados();
        }
    }, [data]);

    const calcularArboles = (co2) => {
        return co2 ? Math.round((co2 * 1000) / 21.77) : 0;
    };

    const calcularROI = (valorProyecto, ahorroMensual) => {
        if (!valorProyecto || !ahorroMensual) return 'No calculado';

        const meses = Math.ceil(valorProyecto / ahorroMensual);
        const años = Math.floor(meses / 12);
        const mesesRestantes = meses % 12;

        if (años === 0) return `${meses} meses`;
        if (mesesRestantes === 0) return `${años} años`;
        return `${años} años y ${mesesRestantes} meses`;
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('es-CO', {
            style: 'currency',
            currency: 'COP',
            minimumFractionDigits: 0
        }).format(value);
    };

    const formatNumber = (value, decimals = 2) => {
        return new Intl.NumberFormat('es-CO', {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals
        }).format(value);
    };

    const formatCO2 = (value) => {
        return `${formatNumber(value, 3)} toneladas`;
    };

    const StatCard = ({ icon, title, value, color }) => {
        const IconComponent = icon;
        return (
            <Card sx={{
                minWidth: 200,
                borderRadius: 3,
                boxShadow: 3,
                background: `linear-gradient(135deg, ${color} 0%, ${theme.palette.background.paper} 100%)`,
                transition: 'transform 0.3s',
                '&:hover': {
                    transform: 'scale(1.03)'
                }
            }}>
                <CardContent sx={{ textAlign: 'center', py: 3 }}>
                    <IconComponent sx={{ fontSize: 40, color: theme.palette.getContrastText(color) }} />
                    <Typography variant="h6" sx={{ mt: 1, fontWeight: 'bold' }}>
                        {title}
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: theme.palette.getContrastText(color) }}>
                        {value}
                    </Typography>
                </CardContent>
            </Card>
        );
    };

    return (
        <Box sx={{
            position: 'relative',
            overflow: 'hidden',
            p: 4,
            background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8ed 100%)',
            borderRadius: 4,
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
            '&::before': {
                content: '""',
                position: 'absolute',
                top: -50,
                right: -50,
                width: 200,
                height: 200,
                background: 'radial-gradient(circle, rgba(255,204,0,0.2) 0%, rgba(255,204,0,0) 70%)',
                borderRadius: '50%'
            },
            '&::after': {
                content: '""',
                position: 'absolute',
                bottom: -30,
                left: -30,
                width: 150,
                height: 150,
                background: 'radial-gradient(circle, rgba(0,150,255,0.1) 0%, rgba(0,150,255,0) 70%)',
                borderRadius: '50%'
            }
        }}>
            {/* Efectos de rayos solares */}
            {[0, 45, 90, 135, 180, 225, 270, 315].map((angle) => (
                <Box key={angle} sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    width: '100%',
                    height: 1,
                    background: `linear-gradient(90deg, rgba(255,204,0,0) 0%, rgba(255,204,0,0.1) 50%, rgba(255,204,0,0) 100%)`,
                    transform: `translate(-50%, -50%) rotate(${angle}deg)`,
                    transformOrigin: 'center',
                    zIndex: 0
                }} />
            ))}

            <Box sx={{ position: 'relative', zIndex: 1 }}>
                <Slide in={true} direction="down" timeout={500}>
                    <Box sx={{ textAlign: 'center', mb: 4 }}>
                        <SolarPowerIcon sx={{ fontSize: 60, color: '#FFC107', mb: 1 }} />
                        <Typography variant="h4" sx={{
                            fontWeight: 'bold',
                            mb: 1,
                            background: 'linear-gradient(90deg, #FF8C00, #FFC107)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent'
                        }}>
                            Resultados Preliminares
                        </Typography>
                        <Typography variant="subtitle1" sx={{ color: 'text.secondary' }}>
                            Basado en tu factura eléctrica y ubicación, estos son nuestros cálculos iniciales
                        </Typography>
                    </Box>
                </Slide>

                {loadingCalculos && (
                    <Fade in={loadingCalculos}>
                        <Box sx={{ mb: 4 }}>
                            <LinearProgress
                                variant="determinate"
                                value={(apiCallsCompleted / totalApiCalls) * 100}
                                sx={{
                                    height: 10,
                                    borderRadius: 5,
                                    background: 'rgba(255,204,0,0.2)',
                                    '& .MuiLinearProgress-bar': {
                                        background: 'linear-gradient(90deg, #FF8C00, #FFC107)'
                                    }
                                }}
                            />
                            <Typography variant="caption" display="block" textAlign="center" sx={{ mt: 1 }}>
                                Analizando datos solares: {apiCallsCompleted} de {totalApiCalls} completados
                            </Typography>
                        </Box>
                    </Fade>
                )}

                {errorCalculos && (
                    <Fade in={!!errorCalculos}>
                        <Typography color="error" sx={{
                            mb: 3,
                            p: 2,
                            background: 'rgba(255,0,0,0.1)',
                            borderRadius: 2,
                            textAlign: 'center'
                        }}>
                            ⚠️ {errorCalculos}
                        </Typography>
                    </Fade>
                )}

                <Grid container spacing={3} sx={{ mb: 4 }}>
                    <Grid item xs={12} md={6} lg={3}>
                        <StatCard
                            icon={AttachMoneyIcon}
                            title="Ahorro Mensual"
                            value={ahorroMensual ? formatCurrency(ahorroMensual) : '...'}
                            color="#4CAF50"
                        />
                    </Grid>
                    <Grid item xs={12} md={6} lg={3}>
                        <StatCard
                            icon={SolarPowerIcon}
                            title="Paneles Solares"
                            value={panelesEstimados !== null ? panelesEstimados : '...'}
                            color="#FF9800"
                        />
                    </Grid>
                    <Grid item xs={12} md={6} lg={3}>
                        <StatCard
                            icon={ElectricBoltIcon}
                            title="Potencia Instalada"
                            value={potenciaInstalada !== null ? `${formatNumber(potenciaInstalada, 2)} kWp` : '...'}
                            color="#2196F3"
                        />
                    </Grid>
                    <Grid item xs={12} md={6} lg={3}>
                        <StatCard
                            icon={EnergySavingsLeafIcon}
                            title="Ahorro de CO₂"
                            value={ahorroCO2 !== null ? formatCO2(ahorroCO2) : '...'}
                            color="#8BC34A"
                        />
                    </Grid>
                </Grid>

                <Paper elevation={0} sx={{
                    p: 3,
                    mb: 4,
                    borderRadius: 3,
                    background: 'rgba(255,255,255,0.8)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255,255,255,0.2)'
                }}>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <AttachMoneyIcon color="primary" sx={{ mr: 1 }} />
                                <Typography variant="h6">Valor de factura eléctrica</Typography>
                            </Box>
                            <Typography variant="body1" sx={{ pl: 4 }}>
                                {data.valorFactura || 'No especificado'}
                            </Typography>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <ParkIcon color="primary" sx={{ mr: 1 }} />
                                <Typography variant="h6">Árboles salvados</Typography>
                            </Box>
                            <Typography variant="body1" sx={{ pl: 4 }}>
                                {ahorroCO2 !== null ? calcularArboles(ahorroCO2) : 'Calculando...'}
                            </Typography>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <ScheduleIcon color="primary" sx={{ mr: 1 }} />
                                <Typography variant="h6">Recuperación de inversión</Typography>
                            </Box>
                            <Typography variant="body1" sx={{ pl: 4 }}>
                                {calcularROI(totalProyecto, ahorroMensual)}
                            </Typography>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <SolarPowerIcon color="primary" sx={{ mr: 1 }} />
                                <Typography variant="h6">Empresa prestadora</Typography>
                            </Box>
                            <Typography variant="body1" sx={{ pl: 4 }}>
                                {empresaPrestadora?.Nombre || 'Calculando...'}
                            </Typography>
                        </Grid>
                    </Grid>

                    <Accordion
                        expanded={showDetails}
                        onChange={() => setShowDetails(!showDetails)}
                        sx={{
                            mt: 3,
                            background: 'transparent',
                            boxShadow: 'none',
                            '&:before': {
                                display: 'none'
                            }
                        }}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            sx={{
                                background: 'rgba(0,0,0,0.03)',
                                borderRadius: 2
                            }}
                        >
                            <Typography>Detalles técnicos y supuestos</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Typography variant="body2" paragraph>
                                <strong>Nota:</strong> Estos cálculos son estimados basados en la información proporcionada.
                                Los valores finales pueden variar después de una evaluación técnica detallada.
                            </Typography>

                        </AccordionDetails>
                    </Accordion>
                </Paper>

                <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: 2
                }}>
                    <Button
                        onClick={onBack}
                        variant="outlined"
                        sx={{
                            px: 4,
                            py: 1.5,
                            borderRadius: 50,
                            borderWidth: 2,
                            '&:hover': { borderWidth: 2 }
                        }}
                    >
                        Volver atrás
                    </Button>

                    <Box sx={{
                        display: 'flex',
                        gap: 2,
                        flexDirection: { xs: 'column', sm: 'row' }
                    }}>

                        <Button
                            variant="contained"
                            startIcon={<WhatsAppIcon />}
                            onClick={handleWhatsAppClick}  // Cambiado a nuestra nueva función
                            sx={{
                                px: 4,
                                py: 1.5,
                                borderRadius: 50,
                                background: '#25D366',
                                '&:hover': { background: '#128C7E' }
                            }}
                        >
                            Consultar por WhatsApp
                        </Button>

                        <Button
                            variant="contained"
                            onClick={onNext}
                            disabled={loadingCalculos || errorCalculos}
                            sx={{
                                px: 4,
                                py: 1.5,
                                borderRadius: 50,
                                background: 'linear-gradient(90deg, #FF8C00, #FFC107)',
                                '&:hover': { background: 'linear-gradient(90deg, #FF6D00, #FFAB00)' },
                                '&:disabled': { background: '#e0e0e0' }
                            }}
                        >
                            ¡Continuar con el proyecto!
                        </Button>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default Paso2BResultadosParciales;